import {
  RECRUITING_VACANCY_PUBLICATION_READ,
  VACANCY_GROUP_STATUS,
  RECRUITING_VACANCY_VIEW_CHANGE_REQUESTS,
} from 'domain/shared/newVacancy';
import { useVacancyGroupId, useVacancyGroupUrl, usePublicationsUrl } from 'domain/recruiting/newVacancy/routing';
import { RECRUITING_CONTEXT } from 'domain/recruiting/RecruitingContext';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { TabProps } from 'framework/ui/BreadCrumbs';
import { useAuthorization } from 'framework/authorization';
import { FormFieldsData } from 'framework/form';

const useVacancyGroupBreadcrumbsMenu = (): TabProps[] => {
  const navigate = useNavigate();
  const id = useVacancyGroupId()!;
  const { getSingleEntityUrl, getSourcingUrl, getHistoryUrl } = useVacancyGroupUrl();
  const publicationsUrl = usePublicationsUrl();
  const { isGranted: globalIsGranted } = useAuthorization();

  const isGranted = useCallback(
    (permission: string | string[]) => globalIsGranted(permission, RECRUITING_CONTEXT),
    [globalIsGranted],
  );

  const onChangeTabCallback = useCallback(
    (route: string) => {
      navigate(route, { preventScrollReset: true });
    },
    [navigate],
  );

  const canViewSourcingForClosed = useCallback(() => {
    return isGranted(RECRUITING_VACANCY_VIEW_CHANGE_REQUESTS);
  }, [isGranted]);

  const isSourcingTabVisible = useCallback(
    (data: FormFieldsData) => {
      if (canViewSourcingForClosed()) return true;
      return data?.details?.status !== VACANCY_GROUP_STATUS.CLOSED;
    },
    [canViewSourcingForClosed],
  );

  return [
    {
      label: 'Information',
      route: getSingleEntityUrl(id),
    },
    {
      label: 'Publications',
      route: publicationsUrl,
      isVisible: isGranted(RECRUITING_VACANCY_PUBLICATION_READ),
    },
    {
      label: 'Sourcing',
      route: getSourcingUrl(id),
      isVisible: isSourcingTabVisible,
    },
    {
      label: 'History',
      route: getHistoryUrl(id),
    },
  ].map(item => {
    return {
      onChangeCallback: onChangeTabCallback,
      value: item.route,
      ...item,
    };
  });
};

export default useVacancyGroupBreadcrumbsMenu;
